import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import StaffSelector from '../../events/StaffSelector'
import { UserContext } from '../../../App'
import EmployeeSelector from '../../events/EmployeeSelector'

export const getId = (value) => value?._id ?? value ?? null

export const employeeName = (employee) =>
  [employee?.userAccount?.firstName, employee?.userAccount?.lastName]
    .filter(Boolean)
    .join(' ')

const blank = { firstName: '', lastName: '', email: '', phone: '' }

/**
 * Add / edit a person involved in an incident.
 *
 * Two modes: pick an employee (name is taken from their record, contact details
 * pre-filled but editable, plus an optional "hide this incident from them"
 * toggle) or type someone in manually (a patient, member of the public, etc).
 *
 * onSave({ person, hideFromEmployee }) — `person` carries `employee` (the
 * selected employee object, or null for a manual entry); `hideFromEmployee` is
 * only meaningful when an employee is linked.
 */
const PersonInvolvedDialog = ({
  open,
  onClose,
  onSave,
  person = null,
  isHidden = false,
}) => {
  const { user } = useContext(UserContext)
  const [mode, setMode] = useState('employee')
  const [employee, setEmployee] = useState(null)
  const [details, setDetails] = useState(blank)
  const [hide, setHide] = useState(false)
  const [error, setError] = useState('')

  const isEdit = !!person

  useEffect(() => {
    if (!open) return
    setError('')
    if (person) {
      const linked = person.employee ? person.employee : null
      setMode(linked ? 'employee' : 'manual')
      setEmployee(linked)
      setDetails({
        firstName: person.firstName ?? '',
        lastName: person.lastName ?? '',
        email: person.email ?? '',
        phone: person.phone ?? '',
      })
      setHide(!!isHidden)
    } else {
      setMode('employee')
      setEmployee(null)
      setDetails(blank)
      setHide(false)
    }
  }, [open, person, isHidden])

  const isSelf =
    !!employee?.userAccount?._id && employee.userAccount._id === user?._id

  const selectEmployee = (value) => {
    setEmployee(value)
    setError('')
    if (value) {
      setDetails({
        firstName: value?.userAccount?.firstName ?? '',
        lastName: value?.userAccount?.lastName ?? '',
        email: value?.userAccount?.email ?? '',
        phone:
          value?.personnelInfo?.phoneNumber1 ??
          value?.personnelInfo?.phoneNumber2 ??
          '',
      })
      if (value?.userAccount?._id === user?._id) setHide(false)
    } else {
      setDetails(blank)
      setHide(false)
    }
  }

  const switchMode = (next) => {
    if (!next || next === mode) return
    setMode(next)
    setError('')
    setEmployee(null)
    setHide(false)
    // Keep anything already typed when moving to manual entry; start clean
    // when moving to an employee pick so the record's details take over.
    if (next === 'employee') setDetails(blank)
  }

  const setField = (field) => (e) =>
    setDetails((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSave = (e) => {
    e.preventDefault()
    if (mode === 'employee' && !employee) {
      setError(
        'Select an employee, or switch to "Other person" to type their details.',
      )
      return
    }
    if (!details.firstName.trim() || !details.lastName.trim()) {
      setError('First and last name are required.')
      return
    }
    onSave({
      person: {
        ...(person?._id ? { _id: person._id } : {}),
        firstName: details.firstName.trim(),
        lastName: details.lastName.trim(),
        email: details.email.trim(),
        phone: details.phone.trim(),
        employee: mode === 'employee' ? employee : null,
      },
      hideFromEmployee: mode === 'employee' && !!employee && hide && !isSelf,
    })
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        component: 'form',
        onSubmit: handleSave,
        autoComplete: 'off',
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {isEdit ? 'Edit Person Involved' : 'Add Person Involved'}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={0.5}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, next) => switchMode(next)}
            fullWidth
            size="small"
            color="primary"
          >
            <ToggleButton
              value="employee"
              sx={{ textTransform: 'none', gap: 1 }}
            >
              <BadgeOutlinedIcon fontSize="small" /> Employee
            </ToggleButton>
            <ToggleButton value="manual" sx={{ textTransform: 'none', gap: 1 }}>
              <PersonOutlineIcon fontSize="small" /> Other person
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'employee' ? (
            <>
              <EmployeeSelector
                value={employee}
                valueType="employee"
                onChange={(e, value) => selectEmployee(value)}
                label="Employee"
                fullWidth
                size="small"
                required
              />
              {employee && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  p={1.5}
                  borderRadius={2}
                  bgcolor="action.hover"
                >
                  <Avatar
                    src={employee?.userAccount?.photograph}
                    sx={{ width: 40, height: 40 }}
                  />
                  <Box minWidth={0}>
                    <Typography fontWeight={600} noWrap>
                      {employeeName(employee)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employee?.clinicalRole ?? 'Employee'} — name taken from
                      their staff record
                    </Typography>
                  </Box>
                </Box>
              )}
              {!employee && (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                  <TextField
                    label="Email"
                    value={details.email}
                    onChange={setField('email')}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    disabled={!employee}
                    inputProps={{ autoComplete: 'off' }}
                  />
                  <TextField
                    label="Phone"
                    value={details.phone}
                    onChange={setField('phone')}
                    size="small"
                    fullWidth
                    autoComplete="off"
                    disabled={!employee}
                    inputProps={{ autoComplete: 'off' }}
                  />
                </Stack>
              )}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hide}
                      onChange={(e) => setHide(e.target.checked)}
                      disabled={!employee || isSelf}
                      color="warning"
                    />
                  }
                  label="Hide this incident from them"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  pl={4}
                  mt={-0.5}
                >
                  {isSelf
                    ? "You can't hide an incident from yourself."
                    : 'They will not see this incident in the incident list and will not be able to open it. This can be changed later under Review → Hidden From.'}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                <TextField
                  label="First Name"
                  value={details.firstName}
                  onChange={setField('firstName')}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                  autoFocus
                />
                <TextField
                  label="Last Name"
                  value={details.lastName}
                  onChange={setField('lastName')}
                  size="small"
                  fullWidth
                  required
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                <TextField
                  label="Email"
                  value={details.email}
                  onChange={setField('email')}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                />
                <TextField
                  label="Phone"
                  value={details.phone}
                  onChange={setField('phone')}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  inputProps={{ autoComplete: 'off' }}
                />
              </Stack>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button type="submit" variant="contained">
          {isEdit ? 'Save Person' : 'Add Person'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PersonInvolvedDialog
